<template>
  <div class="custom-table-wrapper">
    <div class="table-content">
      <div v-if="show_header" class="table-header">
        <h4 v-if="show_title">
          {{ title || $t('shared.component.table.overview') }}
        </h4>
        <div class="per-page-slot">
          <slot name="per-page-select">
            <div v-if="show_per_page">
              <label>
                {{ $t('shared.component.table.show') }}
                <b-form-select v-model="data_per_page" :options="[5, 10, 25, 50, 100]" />
                {{ $t('shared.component.table.records') }}
              </label>
            </div>
          </slot>
        </div>
      </div>

      <div class="table-loading-wrapper">
        <b-table
          show-empty
          :responsive="true"
          hover
          :foot-clone="foot_clone"
          :fields="transformFields"
          :items="items"
          :busy="data_is_loading"
          :per-page="data_per_page"
          :no-local-sorting="true"
          :no-sort-reset="true"
          :class="['table-fw', ...table_class]"
          :tbody-tr-class="tbody_tr_class"
          :current-page="data_current_page"
          :empty-text="
            data_is_loading
              ? $t('shared.component.table.loading')
              : $t('shared.component.table.empty')
          "
        >
          <template #row-details="params">
            <slot name="row-details" v-bind="params"></slot>
          </template>

          <template v-for="cell in fields" v-slot:[get_head_slot_name(cell.key)]="headScope">
            <slot :name="`head_${cell.key}`" v-bind="headScope">
              {{ headScope.label }}
            </slot>
          </template>

          <template
            v-for="cell in fields"
            v-slot:[get_field_slot_name(cell.key)]="{
              item,
              value: formatted,
              ...others
            }"
          >
            <slot :name="cell.key" v-bind="{ item, formatted, ...others }">
              {{ formatted }}
            </slot>
          </template>
        </b-table>

        <div v-if="data_is_loading" class="table-loading-overlay">
          <b-spinner variant="primary" />
        </div>
      </div>
    </div>

    <div v-if="total_items > data_per_page" class="row align-items-center mt-2">
      <div v-if="show_pagination_description" class="paginate-counter col-md-6">
        {{
          $t('shared.component.table.paginate_info', {
            start: current_items_index_start,
            end: current_items_index_end,
            all: total_items
          })
        }}
      </div>
      <div :class="{ 'col-md-6': true, 'offset-6': !show_pagination_description }">
        <b-pagination
          v-model="data_current_page"
          :per-page="data_per_page"
          :total-rows="total_items"
          :limit="7"
          align="right"
          style="margin: 0"
        />
      </div>
    </div>
  </div>
</template>

<script>
  import { toUserDateWithTime } from '@shared/helpers/date_format';

  export default {
    props: {
      title: {
        type: String,
        default: null
      },

      fields: {
        type: Array,
        default: () => []
      },

      items: {
        type: Array,
        default: () => []
      },

      table_class: {
        type: [Array, String],
        default: ''
      },

      tbody_tr_class: {
        type: Function,
        default: null
      },

      show_per_page: {
        type: Boolean,
        default: true
      },

      per_page: {
        type: Number,
        default: 10
      },

      show_title: {
        type: Boolean,
        default: true
      },

      show_header: {
        type: Boolean,
        default: true
      },

      show_pagination_description: {
        type: Boolean,
        default: true
      },

      foot_clone: {
        type: Boolean,
        default: false
      },

      is_loading: {
        type: Boolean,
        default: false
      }
    },

    data() {
      return {
        data_current_page: 1,
        data_per_page: 10,
        data_is_loading: false
      };
    },

    computed: {
      transformFields: function() {
        return this.fields.map(item => {
          const default_date_fields = ['created_at', 'updated_at'];

          if (default_date_fields.includes(item.key)) {
            const is_have_formatter = 'formatter' in item;

            if (!is_have_formatter) {
              return {
                ...item,
                formatter: value => toUserDateWithTime(value)
              };
            }
          }

          return item;
        });
      },

      total_items: function() {
        return this.items.length;
      },

      current_items_index_start: function() {
        return this.data_current_page * this.data_per_page - this.data_per_page + 1;
      },

      current_items_index_end: function() {
        const end = this.data_current_page * this.data_per_page;
        return end >= this.total_items ? this.total_items : end;
      }
    },
    watch: {
      is_loading: {
        handler: function(is_loading) {
          this.data_is_loading = is_loading;
        },
        immediate: true
      },

      per_page: {
        handler: function(per_page) {
          this.data_per_page = per_page;
        },
        immediate: true
      }
    },
    methods: {
      get_head_slot_name(key) {
        return `head(${key})`;
      },
      get_field_slot_name(key) {
        return `cell(${key})`;
      }
    }
  };
</script>
